import { useCallback } from 'react';
import { last } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  commonUtils,
  useNotifications,
  editorUtils,
  useEditorStore,
  useFlowBuilder2AddLayoutComponentsMutation,
  Events,
} from '@diana/core';
import { useCreateEmptySection } from './useCreateEmptySection';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';
import { scrollToSection } from './useBuilderScroll';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';

export const useAddSection = (
  options?: Parameters<typeof useFlowBuilder2AddLayoutComponentsMutation>[0],
) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const notifications = useNotifications();

  const getFlow = useEditorStore((s) => s.getFlow);
  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const updateLayoutById = useEditorStore((s) => s.updateLayoutById);

  const createEmptySection = useCreateEmptySection();

  const setActiveComponent = useFlowBuilder2PageStore((s) => s.setActiveComponent);

  const { mutateAsync, isPending: isAddingSection } = useFlowBuilder2AddLayoutComponentsMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      updateLayoutById(data.id, (draft) => ({ ...draft, components: data.components }));
      options?.onSuccess?.(data, variables, context);

      const layoutId = variables.layoutId;
      const sectionId = variables.data[variables.data.length - 1].id;
      setActiveComponent({ layoutId, componentId: sectionId });
      Events.ToggleFlowBuilderNavStepNode.post({ value: true });
      Events.ToggleFlowBuilderNavTaskNode.post({ value: true });
      setTimeout(() => scrollToSection(sectionId));
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('errors.addSectionFailed'),
        description: commonUtils.getErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });

  const addSection = useCallback(
    async (layoutId: string) => {
      const flow = getFlow();
      const task = flow?.steps.find((step) => step.layoutId === layoutId) ?? null;
      const afterComponent = last(getLayoutById(layoutId)?.components)?.id;

      const section = createEmptySection();

      section.dataPath = editorUtils.computeLayoutComponentDataPath({
        flow: task ? flow : null,
        task,
        component: section,
      });

      section.name = editorUtils.computeLayoutComponentName({
        flow: task ? flow : null,
        task,
        component: section,
      });

      return mutateAsync({ layoutId, afterComponent, data: [section] });
    },
    [createEmptySection, getFlow, getLayoutById, mutateAsync],
  );

  return { addSection, isAddingSection };
};
